import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

const registerSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(30, 'Username must be at most 30 characters')
    .regex(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed'),
  display_name: z
    .string()
    .min(1, 'Display name is required')
    .max(60, 'Display name must be at most 60 characters'),
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters'),
});

export default function RegisterPage() {
  const { register: registerUser, loginWithGoogle } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      toast.error(err.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await registerUser(data.username, data.display_name, data.email, data.password);
      setRegistered(true);
      toast.success('Account created! Check your email to confirm.');
    } catch (err) {
      const msg = err.message || 'Registration failed';
      setError('root', { message: msg });
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          <Link to="/" className="inline-block mb-8">
            <img src="/logo.png" alt="ReadTheRoom" className="h-16 w-16 rounded-xl object-cover mx-auto" />
          </Link>
          <div className="bg-forest/10 border border-forest/20 rounded-2xl px-8 py-10">
            <CheckCircle className="w-12 h-12 text-forest mx-auto mb-4" />
            <h2 className="font-heading text-3xl text-ink mb-3">Check your email</h2>
            <p className="text-ink-light text-sm leading-relaxed mb-2">
              We sent a confirmation link to your email address.
            </p>
            <p className="text-ink-muted text-sm leading-relaxed">
              Click the link in the email to activate your account, then come back to sign in.
            </p>
          </div>
          <p className="text-sm text-ink-muted mt-6">
            Already confirmed?{' '}
            <Link to="/login" className="text-terra hover:text-terra/80 font-medium">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-3">
            <img src="/logo.png" alt="ReadTheRoom" className="h-14 w-14 rounded-xl object-cover mx-auto" />
          </Link>
          <p className="text-ink-muted text-sm font-body">
            Join the community of readers
          </p>
        </div>

        <Card className="bg-cream border-cream-dark shadow-sm">
          <CardHeader className="pb-0 pt-6 px-6">
            <h1 className="font-heading text-2xl text-ink">Create Account</h1>
          </CardHeader>
          <CardContent className="px-6 pb-6 pt-4">
            {/* Google */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogle}
              disabled={googleLoading || isSubmitting}
              className="w-full border-cream-dark text-ink hover:border-terra hover:bg-cream-dark flex items-center gap-2 mb-4"
            >
              {googleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 mb-4">
              <Separator className="flex-1 bg-cream-dark" />
              <span className="text-xs text-ink-muted">or</span>
              <Separator className="flex-1 bg-cream-dark" />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {errors.root && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <p className="text-sm text-red-600">{errors.root.message}</p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-ink-light text-sm font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="your_username"
                  autoComplete="username"
                  {...register('username')}
                  className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted ${
                    errors.username ? 'border-red-400' : ''
                  }`}
                />
                <p className="text-xs text-ink-muted">Lowercase letters, numbers, underscores only</p>
                {errors.username && (
                  <p className="text-xs text-red-500">{errors.username.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="display_name" className="text-ink-light text-sm font-medium">
                  Display Name
                </Label>
                <Input
                  id="display_name"
                  type="text"
                  placeholder="Your Name"
                  autoComplete="name"
                  {...register('display_name')}
                  className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted ${
                    errors.display_name ? 'border-red-400' : ''
                  }`}
                />
                {errors.display_name && (
                  <p className="text-xs text-red-500">{errors.display_name.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-ink-light text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...register('email')}
                  className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted ${
                    errors.email ? 'border-red-400' : ''
                  }`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-ink-light text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  {...register('password')}
                  className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted ${
                    errors.password ? 'border-red-400' : ''
                  }`}
                />
                {errors.password && (
                  <p className="text-xs text-red-500">{errors.password.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-terra hover:bg-terra/90 text-cream font-semibold h-10 mt-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-ink-muted mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-terra hover:text-terra/80 font-medium">
                Sign In
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
